import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Plus, Edit, Trash2, Save, X, ArrowLeft, Calendar, Globe, 
  Rss, ExternalLink, Eye, EyeOff, Star, StarOff, Search,
  MapPin, Clock, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const SOURCE_TYPES = [
  { id: 'event_aggregator', label: 'Event Aggregator', icon: Calendar },
  { id: 'event_calendar', label: 'Event Calendar (iCal)', icon: Calendar },
  { id: 'news_rss', label: 'RSS Feed', icon: Rss },
  { id: 'social_feed', label: 'Social Feed', icon: Globe },
];

const CATEGORIES = [
  { id: 'local_events', label: 'Local Events' },
  { id: 'dog_shows', label: 'Dog Shows' },
  { id: 'training_events', label: 'Training Events' },
  { id: 'adoption_events', label: 'Adoption Events' },
  { id: 'meetups', label: 'Meetups' },
  { id: 'competitions', label: 'Competitions' },
  { id: 'general', label: 'General' },
];

const EventSourcesAdmin = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: '',
    description: '',
    source_type: 'event_aggregator',
    category: 'local_events',
    url: '',
    feed_url: '',
    logo_url: '',
    location_filter: '',
    is_active: true,
    is_featured: false,
    display_order: 0,
    auto_import: false,
    import_frequency_hours: 24
  });

  useEffect(() => {
    const isOfficer = user?.is_admin || user?.is_operator_admin || 
                      ['club_officer', 'ngo_officer'].includes(user?.member_class);
    if (!isOfficer) {
      navigate('/');
      return;
    }
    fetchSources();
  }, [user, navigate]);

  const fetchSources = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/content-sources/events?active_only=false`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSources(data);
      }
    } catch (error) {
      console.error('Error fetching sources:', error);
      toast.error('Failed to load event sources');
    } finally {
      setLoading(false);
    }
  };

  const openEditor = (source = null) => {
    if (source) {
      setEditingItem(source);
      setForm({
        name: source.name,
        description: source.description || '',
        source_type: source.source_type,
        category: source.category,
        url: source.url,
        feed_url: source.feed_url || '',
        logo_url: source.logo_url || '',
        location_filter: source.location_filter || '',
        is_active: source.is_active,
        is_featured: source.is_featured,
        display_order: source.display_order,
        auto_import: source.auto_import,
        import_frequency_hours: source.import_frequency_hours
      });
    } else {
      setEditingItem(null);
      setForm({
        name: '',
        description: '',
        source_type: 'event_aggregator',
        category: 'local_events',
        url: '',
        feed_url: '',
        logo_url: '',
        location_filter: '',
        is_active: true,
        is_featured: false,
        display_order: sources.length,
        auto_import: false,
        import_frequency_hours: 24
      });
    }
    setShowEditor(true);
  };

  const closeEditor = () => {
    setShowEditor(false);
    setEditingItem(null);
  };

  const saveSource = async () => {
    setSaving(true);
    try {
      const url = editingItem 
        ? `${API}/api/content-sources/events/${editingItem.id}`
        : `${API}/api/content-sources/events`;
      
      const method = editingItem ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(form)
      });

      if (response.ok) {
        await fetchSources();
        closeEditor();
        toast.success(editingItem ? 'Source updated!' : 'Source added!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save source');
      }
    } catch (error) {
      console.error('Error saving source:', error);
      toast.error('Failed to save source');
    } finally {
      setSaving(false);
    }
  };

  const deleteSource = async (sourceId) => {
    if (!window.confirm('Are you sure you want to delete this source? This will also delete all imported events from this source.')) return;

    try {
      const response = await fetch(`${API}/api/content-sources/events/${sourceId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setSources(sources.filter(s => s.id !== sourceId));
        toast.success('Source deleted');
      }
    } catch (error) {
      console.error('Error deleting source:', error);
      toast.error('Failed to delete source');
    }
  };

  const toggleActive = async (source) => {
    try {
      const response = await fetch(`${API}/api/content-sources/events/${source.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: !source.is_active })
      });

      if (response.ok) {
        fetchSources();
      }
    } catch (error) {
      console.error('Error toggling source:', error);
    }
  };

  const toggleFeatured = async (source) => {
    try {
      const response = await fetch(`${API}/api/content-sources/events/${source.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_featured: !source.is_featured })
      });

      if (response.ok) {
        fetchSources();
      }
    } catch (error) {
      console.error('Error toggling featured:', error);
    }
  };

  const filteredSources = sources.filter(source =>
    source.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    source.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getSourceIcon = (type) => {
    const found = SOURCE_TYPES.find(t => t.id === type);
    return found ? found.icon : Calendar;
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/admin/feed-sources')}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Event Sources</h1>
                <p className="text-sm text-gray-500">
                  Manage curated event feeds from external sources
                </p>
              </div>
            </div>
            <Button onClick={() => openEditor()} className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Add Event Source
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search event sources..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Sources List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredSources.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">No event sources added yet</p>
            <p className="text-sm text-gray-400 mb-4">
              Add sources like Eventbrite, Meetup, or local event calendars to curate events for your members
            </p>
            <Button onClick={() => openEditor()}>
              Add Your First Event Source
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Source</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600 hidden md:table-cell">Category</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600 hidden lg:table-cell">Location</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Events</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Active</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Featured</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredSources.map(source => {
                  const Icon = getSourceIcon(source.source_type);
                  return (
                    <tr key={source.id} className={`hover:bg-gray-50 ${!source.is_active ? 'opacity-50' : ''}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <Icon className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{source.name}</p>
                            <a 
                              href={source.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                            >
                              Visit <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full capitalize">
                          {source.category.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        {source.location_filter ? (
                          <span className="flex items-center gap-1 text-sm text-gray-600">
                            <MapPin className="w-3 h-3" />
                            {source.location_filter}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">All locations</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-sm font-medium text-gray-700">{source.events_count}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => toggleActive(source)}
                          className={`p-1.5 rounded-lg transition ${
                            source.is_active
                              ? 'bg-green-100 text-green-600 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                          }`}
                        >
                          {source.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => toggleFeatured(source)}
                          className={`p-1.5 rounded-lg transition ${
                            source.is_featured
                              ? 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200'
                              : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                          }`}
                        >
                          {source.is_featured ? <Star className="w-4 h-4 fill-current" /> : <StarOff className="w-4 h-4" />}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openEditor(source)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteSource(source.id)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Suggested Sources */}
        {sources.length === 0 && !loading && (
          <div className="mt-8 bg-blue-50 rounded-lg p-6">
            <h3 className="font-semibold text-blue-900 mb-3">Suggested Event Sources</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { name: 'Eventbrite', url: 'https://www.eventbrite.com', desc: 'Popular event platform' },
                { name: 'Meetup', url: 'https://www.meetup.com', desc: 'Community meetups' },
                { name: 'AKC Events', url: 'https://www.akc.org/sports/events/', desc: 'Dog shows & competitions' },
                { name: 'BringFido Events', url: 'https://www.bringfido.com/event/', desc: 'Pet-friendly events' },
              ].map(suggestion => (
                <div key={suggestion.name} className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="font-medium text-gray-900">{suggestion.name}</p>
                  <p className="text-sm text-gray-500 mb-2">{suggestion.desc}</p>
                  <a 
                    href={suggestion.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline"
                  >
                    {suggestion.url}
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Editor Modal */}
      {showEditor && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-8">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 my-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-xl font-bold text-gray-900">
                {editingItem ? 'Edit Event Source' : 'Add Event Source'}
              </h2>
              <button onClick={closeEditor} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({...form, name: e.target.value})}
                  placeholder="e.g., Eventbrite Dog Events"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL *</label>
                <input
                  type="url"
                  value={form.url}
                  onChange={(e) => setForm({...form, url: e.target.value})}
                  placeholder="https://eventbrite.com/d/dog-events/"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={form.source_type}
                    onChange={(e) => setForm({...form, source_type: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {SOURCE_TYPES.map(type => (
                      <option key={type.id} value={type.id}>{type.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({...form, category: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({...form, description: e.target.value})}
                  placeholder="Brief description of this event source"
                  rows={2}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location Filter</label>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={form.location_filter}
                    onChange={(e) => setForm({...form, location_filter: e.target.value})}
                    placeholder="e.g., California, New York City"
                    className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Filter events to a specific region (optional)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Feed URL (RSS/iCal)</label>
                <input
                  type="url"
                  value={form.feed_url}
                  onChange={(e) => setForm({...form, feed_url: e.target.value})}
                  placeholder="https://example.com/events.ics"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">For automatic event imports (optional)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL</label>
                <input
                  type="url"
                  value={form.logo_url}
                  onChange={(e) => setForm({...form, logo_url: e.target.value})}
                  placeholder="https://example.com/logo.png"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm({...form, is_active: e.target.checked})}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <span className="text-sm text-gray-700">Active</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.is_featured}
                    onChange={(e) => setForm({...form, is_featured: e.target.checked})}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <span className="text-sm text-gray-700">Featured</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.auto_import}
                    onChange={(e) => setForm({...form, auto_import: e.target.checked})}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <span className="text-sm text-gray-700">Auto-import events</span>
                </label>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              <Button variant="outline" onClick={closeEditor}>
                Cancel
              </Button>
              <Button
                onClick={saveSource}
                disabled={saving || !form.name || !form.url}
              >
                {saving ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {editingItem ? 'Update Source' : 'Add Source'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventSourcesAdmin;
